import React, { useEffect, useState } from "react";
import { Row, Col } from "reactstrap";
import StatusBadge from "../Customers/StatusBadge";
import CommonButton from "../Utils/CommonButton";
import { useNavigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import DateRangeSelector from "../EVDashboard/DateRangeSelector";
import {
  formatDateForPayload,
  getCurrentWeekRange,
} from "../Utils/UtilityFunctions";
import { getAllStationUsageTrend } from "../../slices/EVDashboard/StationUsage/thunk";
import { useDispatch, useSelector } from "react-redux";
import { getAllStationRevenueTrend } from "../../slices/EVDashboard/StationRevenue/thunk";
import noData from "../../assets/images/NoData.gif";
import { getAllStationUsageForecast } from "../../slices/EVDashboard/StationUsageForecast/thunk";
import { getAllStationRevenueForecast } from "../../slices/EVDashboard/StationRevenueForecast/thunk";

// ─────────────────────────────────────────────
const generateRevenueChartData = (
  apiData = [],
  period = "3m",
  fromDate,
  toDate
) => {
  if (period === "custom" && fromDate && toDate) {
    const fmt = (dateStr) => {
      const d = new Date(dateStr);
      return d.toLocaleString("default", { month: "short", day: "numeric" });
    };
    const start = new Date(fromDate);
    const end = new Date(toDate);
    const days = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      days.push(new Date(d).toISOString().split("T")[0]);
    }
    const data = days.map((day) => {
      const found = apiData.find((item) => item.date === day);
      return found ? found.ratedUnit ?? 0 : 0;
    });
    return {
      categories: days.map((d) => fmt(d)),
      series: [{ name: "Revenue", data }],
    };
  }

  if (period === "1m") {
    const fmt = (dateStr) => {
      const d = new Date(dateStr);
      return d.toLocaleString("default", { month: "short", day: "numeric" });
    };
    const now = new Date();
    const currentSunday = new Date(now);
    currentSunday.setDate(now.getDate() - now.getDay());
    const weeks = [];
    for (let i = 3; i >= 0; i--) {
      const d = new Date(currentSunday);
      d.setDate(currentSunday.getDate() - i * 7);
      weeks.push(d.toISOString().split("T")[0]);
    }
    const data = weeks.map((week) => {
      const found = apiData.find((item) => item.weekStart === week);
      return found ? found.ratedUnit ?? 0 : 0;
    });
    return {
      categories: weeks.map((w) => fmt(w)),
      series: [{ name: "Revenue", data }],
    };
  }
  const monthCount = { "3m": 3, "6m": 6, "12m": 12 };
  const count = monthCount[period] || 3;
  const months = [];
  const now = new Date();
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      month: d.toLocaleString("default", { month: "short" }),
      year: d.getFullYear(),
    });
  }
  const categories = months.map((m) => m.month);
  const data = months.map((m) => {
    const found = apiData.find(
      (item) => item.month === m.month && item.year === m.year
    );
    return found ? found.ratedUnit : 0;
  });
  return {
    categories,
    series: [{ name: "Revenue", data }],
  };
};

const generateUsageChartData = (
  apiData = [],
  period = "3m",
  fromDate,
  toDate
) => {
  if (period === "custom" && fromDate && toDate) {
    const fmt = (dateStr) => {
      const d = new Date(dateStr);
      return d.toLocaleString("default", { month: "short", day: "numeric" });
    };
    const start = new Date(fromDate);
    const end = new Date(toDate);
    const days = [];
    const current = new Date(start);
    while (current <= end) {
      const yyyy = current.getFullYear();
      const mm = String(current.getMonth() + 1).padStart(2, "0");
      const dd = String(current.getDate()).padStart(2, "0");
      days.push(`${yyyy}-${mm}-${dd}`);
      current.setDate(current.getDate() + 1);
    }
    const usageData = days.map((day) => {
      const found = apiData.find((item) => item.date === day);
      return found ? found.totalUsage ?? 0 : 0;
    });
    const sessionData = days.map((day) => {
      const found = apiData.find((item) => item.date === day);
      return found ? found.sessionCount ?? 0 : 0;
    });
    return {
      categories: days.map((d) => fmt(d)),
      series: [
        { name: "Usage (kWh)", data: usageData },
        { name: "Sessions", data: sessionData },
      ],
    };
  }

  if (period === "1m") {
    const fmt = (dateStr) => {
      const d = new Date(dateStr);
      return d.toLocaleString("default", { month: "short", day: "numeric" });
    };
    const now = new Date();
    const currentSunday = new Date(now);
    currentSunday.setDate(now.getDate() - now.getDay());
    const weeks = [];
    for (let i = 3; i >= 0; i--) {
      const d = new Date(currentSunday);
      d.setDate(currentSunday.getDate() - i * 7);
      weeks.push(d.toISOString().split("T")[0]);
    }
    const usageData = weeks.map((week) => {
      const found = apiData.find((item) => item.weekStart === week);
      return found ? found.totalUsage ?? 0 : 0;
    });
    const sessionData = weeks.map((week) => {
      const found = apiData.find((item) => item.weekStart === week);
      return found ? found.sessionCount ?? 0 : 0;
    });
    return {
      categories: weeks.map((w) => fmt(w)),
      series: [
        { name: "Usage (kWh)", data: usageData },
        { name: "Sessions", data: sessionData },
      ],
    };
  }

  const monthCount = { "3m": 3, "6m": 6, "12m": 12 };
  const count = monthCount[period] || 3;
  const months = [];
  const now = new Date();
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      month: d.toLocaleString("default", { month: "short" }),
      year: d.getFullYear(),
    });
  }
  const categories = months.map((m) => m.month);
  const usageData = months.map((m) => {
    const found = apiData.find(
      (item) => item.month === m.month && item.year === m.year
    );
    return found ? found.totalUsage : 0;
  });
  const sessionData = months.map((m) => {
    const found = apiData.find(
      (item) => item.month === m.month && item.year === m.year
    );
    return found ? found.sessionCount : 0;
  });
  return {
    categories,
    series: [
      { name: "Usage (kWh)", data: usageData },
      { name: "Sessions", data: sessionData },
    ],
  };
};

const generateUsageForecastChartData = (apiData = [], period = "3m") => {
  const forecastList =
    apiData?.forecast || (Array.isArray(apiData) ? apiData : []);
  if (!forecastList.length) {
    return {
      categories: [],
      series: [{ name: "Usage Forecast (kWh)", data: [] }],
    };
  }

  const formatDay = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleString("default", { month: "short", day: "numeric" });
  };

  const formatMonth = (year, month) => {
    return new Date(year, month, 1).toLocaleString("default", {
      month: "short",
      year: "2-digit",
    });
  };

  const periodWeeksMap = {
    "1m": 4,
    "3m": 12,
    "6m": 24,
    "1y": 52,
  };

  const weeksToTake = periodWeeksMap[period] || forecastList.length;
  const slice = forecastList.slice(0, weeksToTake);

  // 🔹 1M & 3M → weekly
  if (period === "1m") {
    return {
      categories: slice.map((item) => formatDay(item.date)),
      series: [
        {
          name: "Usage Forecast (kWh)",
          data: slice.map((item) => parseFloat((item.value || 0).toFixed(2))),
        },
      ],
    };
  }

  // 🔹 6M & 1Y → monthly (take LAST value of each month)
  const months = {};

  slice.forEach((item) => {
    const d = new Date(item.date);
    const year = d.getFullYear();
    const month = d.getMonth();
    const key = `${year}-${month}`;

    // Always overwrite → ensures LAST week wins
    months[key] = {
      value: item.value || 0,
      year,
      month,
      date: d,
    };
  });

  const sortedKeys = Object.keys(months).sort(
    (a, b) => new Date(months[a].date) - new Date(months[b].date)
  );

  return {
    categories: sortedKeys.map((k) => {
      const { year, month } = months[k];
      return formatMonth(year, month);
    }),
    series: [
      {
        name: "Usage Forecast (kWh)",
        data: sortedKeys.map((k) => parseFloat(months[k].value.toFixed(2))),
      },
    ],
  };
};

const generateRevenueForecastChartData = (apiData = [], period = "3m") => {
  const forecastList =
    apiData?.forecast || (Array.isArray(apiData) ? apiData : []);

  if (!forecastList.length) {
    return {
      categories: [],
      series: [{ name: "Revenue Forecast (₹)", data: [] }],
    };
  }

  const formatDay = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleString("default", { month: "short", day: "numeric" });
  };

  const formatMonth = (year, month) => {
    return new Date(year, month, 1).toLocaleString("default", {
      month: "short",
      year: "2-digit",
    });
  };

  // ✅ correct slicing (weekly data)
  const periodWeeksMap = {
    "1m": 4,
    "3m": 12,
    "6m": 24,
    "1y": 52,
  };

  const weeksToTake = periodWeeksMap[period] || forecastList.length;
  const slice = forecastList.slice(0, weeksToTake);

  // 🔹 1M & 3M → weekly (NO aggregation)
  if (period === "1m") {
    return {
      categories: slice.map((item) => formatDay(item.date)),
      series: [
        {
          name: "Revenue Forecast (₹)",
          data: slice.map((item) => parseFloat((item.value || 0).toFixed(2))),
        },
      ],
    };
  }

  // 🔹 6M & 1Y → monthly (take LAST week's value)
  const months = {};

  slice.forEach((item) => {
    const d = new Date(item.date);
    const year = d.getFullYear();
    const month = d.getMonth();
    const key = `${year}-${month}`;

    // overwrite → keeps LAST value of that month
    months[key] = {
      value: item.value || 0,
      year,
      month,
      date: d,
    };
  });

  const sortedKeys = Object.keys(months).sort(
    (a, b) => new Date(months[a].date) - new Date(months[b].date)
  );

  return {
    categories: sortedKeys.map((k) => {
      const { year, month } = months[k];
      return formatMonth(year, month);
    }),
    series: [
      {
        name: "Revenue Forecast (₹)",
        data: sortedKeys.map((k) => parseFloat(months[k].value.toFixed(2))),
      },
    ],
  };
};

const baseOptions = (categories) => {
  const isTooMany = categories.length > 10;

  return {
    chart: { toolbar: { show: false }, zoom: { enabled: false } },

    xaxis: {
      categories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      tickPlacement: "between",

      labels: {
        rotate: isTooMany ? -45 : 0, // 🔥 condition
        rotateAlways: isTooMany, // only force when needed
        hideOverlappingLabels: !isTooMany, // hide only when horizontal
        trim: false, // prevent "..."
        style: { fontSize: "11px", colors: "#888" },
      },
    },

    yaxis: {
      labels: { style: { fontSize: "11px", colors: "#888" } },
    },

    grid: { borderColor: "#f0f0f0", strokeDashArray: 3 },
    tooltip: { theme: "light" },
    dataLabels: { enabled: false },
  };
};

// ─────────────────────────────────────────────
// REUSABLE COMPONENTS
// ─────────────────────────────────────────────
const NoDataPlaceholder = () => (
  <div
    style={{ height: "220px" }}
    className="d-flex align-items-center justify-content-center"
  >
    <img src={noData} width={220} alt="No Data" />
  </div>
);

const DetailLine = ({ label, value }) => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
    <span style={{ fontSize: 12, color: "#777" }}>{label}</span>
    <span
      style={{ flex: 1, borderBottom: "1px dotted #ddd", margin: "0 8px" }}
    />
    <span style={{ fontSize: 13, fontWeight: 600 }}>{value || "N/A"}</span>
  </div>
);

// Standard chart card with DateRangeSelector (used for Trends)
const ChartCard = ({
  title,
  color,
  onDateRangeChange,
  showLastMonth = true,
  children,
}) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #e9ecef",
      borderRadius: 10,
      padding: 16,
    }}
  >
    <div className="d-flex justify-content-between align-items-center mb-2">
      <p style={{ fontSize: 13, fontWeight: 600, color }}>{title}</p>
      <DateRangeSelector
        onDateRangeChange={onDateRangeChange}
        isShowDropdown={true}
        showLastMonth={showLastMonth}
      />
    </div>
    {children}
  </div>
);

// Forecast period options
const forecastPeriodOptions = [
  { label: "Next 1 month", value: "1m", steps: 4 },
  { label: "Next 3 months", value: "3m", steps: 12 },
  { label: "Next 6 months", value: "6m", steps: 24 },
  { label: "Next 1 year", value: "12m", steps: 52 },
];

// Forecast chart card with custom period dropdown
const ForecastChartCard = ({
  title,
  color,
  period,
  onPeriodChange,
  children,
}) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #e9ecef",
      borderRadius: 10,
      padding: 16,
    }}
  >
    <div className="d-flex justify-content-between align-items-center mb-2">
      <p style={{ fontSize: 13, fontWeight: 600, color }}>{title}</p>
      {/* <select
        value={period}
        onChange={(e) => onPeriodChange(e.target.value)}
        style={{
          fontSize: 12,
          padding: "4px 10px",
          height: 36,
          borderRadius: 8,
          border: "1px solid #e0e0e0",
          backgroundColor: "#F3F3F5",
          color: "#0A0A0A",
          cursor: "pointer",
          outline: "none",
          fontWeight: 400,
        }}
      >
        {forecastPeriodOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select> */}
      <DateRangeSelector
        onDateRangeChange={(e) => onPeriodChange(e.type)}
        isShowDropdown={true}
        dropdownOptions={forecastPeriodOptions}
        defaultRange={period}
      />
    </div>
    {children}
  </div>
);

const ChartSkeleton = () => {
  return (
    <div style={{ padding: "10px" }}>
      {/* Title placeholder */}
      {/* <div
        style={{
          width: "120px",
          height: "12px",
          background: "#eee",
          borderRadius: "4px",
          marginBottom: "10px",
        }}
      /> */}

      {/* Chart area */}
      <div
        style={{
          width: "100%",
          height: "180px",
          background:
            "linear-gradient(90deg, #f0f0f0 25%, #e6e6e6 50%, #f0f0f0 75%)",
          backgroundSize: "200% 100%",
          animation: "shimmer 1.5s infinite",
          borderRadius: "8px",
        }}
      />

      <style>
        {`
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}
      </style>
    </div>
  );
};

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
const StationDetails = ({ values }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [revenueTrendLoading, setRevenueTrendLoading] = useState(false);
  const [usageTrendLoading, setUsageTrendLoading] = useState(false);
  const [revenueForecastLoading, setRevenueForecastLoading] = useState(false);
  const [usageForecastLoading, setUsageForecastLoading] = useState(false);
  const [revenueRange, setRevenueRange] = useState({ period: "3m" });
  const [usageRange, setUsageRange] = useState({ period: "3m" });
  const [forecastRevenuePeriod, setForecastRevenuePeriod] = useState("3m");
  const [forecastUsagePeriod, setForecastUsagePeriod] = useState("3m");

  const revenueApiData = useSelector(
    (state) => state?.StationRevenueTrends?.stationRevenueTrend || []
  );
  const usageApiData = useSelector(
    (state) => state?.StationUsageTrends?.stationUsageTrend || []
  );
  const usageForecastApiData = useSelector(
    (state) => state?.StationUsageForecast?.stationUsageForecast || []
  );
  const revenueForecastApiData = useSelector(
    (state) => state?.StationRevenueForecast?.stationRevenueForecast || []
  );

  // Initial data fetch on mount
  useEffect(() => {
    if (!values?.stationID) return;
    const defaultRange = { period: "3m" };
    dispatch(
      getAllStationRevenueTrend(
        values.stationID,
        setRevenueTrendLoading,
        defaultRange
      )
    );
    dispatch(
      getAllStationUsageTrend(
        values.stationID,
        setUsageTrendLoading,
        defaultRange
      )
    );
    dispatch(
      getAllStationUsageForecast(values.stationID, setUsageForecastLoading, {
        period: "3m",
      })
    );
    dispatch(
      getAllStationRevenueForecast(
        values.stationID,
        setRevenueForecastLoading,
        { period: "3m" }
      )
    );
  }, [values?.stationID]);

  // ── Chart Data ───────────────────────────────
  const revenueChartData = generateRevenueChartData(
    revenueApiData,
    revenueRange?.period,
    revenueRange?.from,
    revenueRange?.to
  );
  const usageChartData = generateUsageChartData(
    usageApiData,
    usageRange?.period,
    usageRange?.from,
    usageRange?.to
  );
  const usageForecastChartData = generateUsageForecastChartData(
    usageForecastApiData,
    forecastUsagePeriod
  );
  const revenueForecastChartData = generateRevenueForecastChartData(
    revenueForecastApiData,
    forecastRevenuePeriod
  );

  // ── Chart Options ────────────────────────────
  const revenueTrendsOptions = {
    ...baseOptions(revenueChartData.categories),
    chart: { type: "area", toolbar: { show: false }, zoom: { enabled: false } },
    stroke: { curve: "smooth", width: 2.5 },
    colors: ["#F97316"],
    legend: {
      show: true,
      position: "bottom",
      fontSize: "11px",
      showForSingleSeries: true,
    },
    fill: {
      type: "gradient",
      gradient: { opacityFrom: 0.25, opacityTo: 0 },
    },
    yaxis: {
      title: { text: "Revenue", style: { fontSize: "11px" } },
      labels: {
        style: { fontSize: "11px", colors: "#888" },
        formatter: (val) => `₹ ${val.toFixed(2)}`,
      },
    },
  };

  const usageTrendsOptions = {
    ...baseOptions(usageChartData.categories),
    chart: { type: "bar", toolbar: { show: false }, zoom: { enabled: false } },
    plotOptions: { bar: { columnWidth: "45%", borderRadius: 4 } },
    colors: ["#1D4163", "#5F92C2"],
    legend: { show: true, position: "bottom", fontSize: "11px" },
    yaxis: [
      {
        title: { text: "Usage (kWh)", style: { fontSize: "11px" } },
        labels: {
          style: { fontSize: "11px", colors: "#888" },
          formatter: (val) => `${(val / 1000).toFixed(2)} kWh`,
        },
      },
      {
        opposite: true,
        title: { text: "Sessions", style: { fontSize: "11px" } },
        labels: {
          style: { fontSize: "11px", colors: "#888" },
          formatter: (val) => Math.round(val),
        },
      },
    ],
    tooltip: {
      theme: "light",
      y: [
        { formatter: (val) => `${(val / 1000).toFixed(2)} kWh` },
        { formatter: (val) => `${Math.round(val)} Sessions` },
      ],
    },
  };

  const revenueForecastOptions = {
    ...baseOptions(revenueForecastChartData.categories),
    chart: { type: "area", toolbar: { show: false }, zoom: { enabled: false } },
    stroke: { curve: "smooth", width: 2.5 },
    colors: ["#8b5cf6"],
    fill: {
      type: "gradient",
      gradient: { opacityFrom: 0.2, opacityTo: 0 },
    },
    markers: { size: 3, colors: ["#8b5cf6"], strokeWidth: 0 },
    yaxis: {
      title: { text: "Revenue", style: { fontSize: "11px" } },
      labels: {
        style: { fontSize: "11px", colors: "#888" },
        formatter: (val) => `₹ ${val.toFixed(2)}`,
      },
    },
    tooltip: {
      theme: "light",
      y: { formatter: (val) => `₹ ${val.toFixed(2)}` },
    },
    legend: {
      show: true,
      position: "bottom",
      fontSize: "11px",
      showForSingleSeries: true,
    },
  };

  const usageForecastOptions = {
    ...baseOptions(usageForecastChartData.categories),
    chart: { type: "area", toolbar: { show: false }, zoom: { enabled: false } },
    stroke: { curve: "smooth", width: 2.5 },
    colors: ["#22c55e"],
    fill: {
      type: "gradient",
      gradient: { opacityFrom: 0.2, opacityTo: 0 },
    },
    markers: { size: 3, colors: ["#22c55e"], strokeWidth: 0 },
    yaxis: {
      title: { text: "Usage (kWh)", style: { fontSize: "11px" } },
      labels: {
        style: { fontSize: "11px", colors: "#888" },
        formatter: (val) => `${(val / 1000).toFixed(2)} kWh`,
      },
    },
    tooltip: {
      theme: "light",
      y: { formatter: (val) => `${(val / 1000).toFixed(2)} kWh` },
    },
    legend: {
      show: true,
      position: "bottom",
      fontSize: "11px",
      showForSingleSeries: true,
    },
  };

  // ── Handlers ─────────────────────────────────
  const handleRevenueRangeChange = (range) => {
    const { startDate, endDate, type } = range;
    const formatted = {
      period: type,
      from: formatDateForPayload(startDate),
      to: formatDateForPayload(endDate),
    };
    setRevenueRange(formatted);
    dispatch(
      getAllStationRevenueTrend(
        values.stationID,
        setRevenueTrendLoading,
        formatted
      )
    );
  };

  const handleUsageRangeChange = (range) => {
    const { startDate, endDate, type } = range;
    const formatted = {
      period: type,
      from: formatDateForPayload(startDate),
      to: formatDateForPayload(endDate),
    };
    setUsageRange(formatted);
    dispatch(
      getAllStationUsageTrend(values.stationID, setUsageTrendLoading, formatted)
    );
  };

  const handleForecastRevenuePeriodChange = (newPeriod) => {
    setForecastRevenuePeriod(newPeriod);
    dispatch(
      getAllStationRevenueForecast(
        values.stationID,
        setRevenueForecastLoading,
        { period: newPeriod }
      )
    );
  };

  const handleForecastUsagePeriodChange = (newPeriod) => {
    setForecastUsagePeriod(newPeriod);
    dispatch(
      getAllStationUsageForecast(values.stationID, setUsageForecastLoading, {
        period: newPeriod,
      })
    );
  };

  const stationType = values?.attributes?.find(
    (attr) => attr.attributeName === "StationType"
  )?.attributeValue;

const formatTimeToAMPM = (time) => {
  if (!time) return "";

  const [hours, minutes] = time.split(":");
  const hour = Number(hours);

  const period = hour >= 12 ? "PM" : "AM";
  const formattedHour = hour % 12 || 12;

  return `${String(formattedHour).padStart(2, "0")}:${minutes} ${period}`;
};

const operationalHoursStartTime = values?.attributes?.find(
  (attr) => attr.attributeName === "Opening Time"
)?.attributeValue;

const operationalHoursEndTime = values?.attributes?.find(
  (attr) => attr.attributeName === "Closing Time"
)?.attributeValue;

const operationalHours =
  operationalHoursStartTime && operationalHoursEndTime
    ? `${formatTimeToAMPM(operationalHoursStartTime)} to ${formatTimeToAMPM(
        operationalHoursEndTime
      )}`
    : "";

  return (
    <div className="px-3 py-3">
      <div className="d-flex align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-1">
            {values?.name || "Station Name"}
            <span style={{ marginLeft: 10 }}>
              <StatusBadge value={values?.status} />
            </span>
          </h4>
          <p style={{ fontSize: 12, color: "#777", marginBottom: 0 }}>
  {values?.address_line1} • {values?.coordinates?.latitude},{" "}
  {values?.coordinates?.longitude} • {values?.timeZone}
  {operationalHours && ` • ${operationalHours}`}
</p>
        </div>
        <div className="ms-auto">
          <CommonButton
            color={"secondary"}
            icon={<i className="bx bxs-edit label-icon fs-16 me-1" />}
            buttonText={"Edit"}
            className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap"
            btnTxtClsName={"px-2 ps-3 ms-2 me-1"}
            buttonAction={() => navigate(`/station/${values?.stationID}`)}
          />
        </div>
      </div>

      <Row className="g-4 mb-4">
        <Col md={6}>
          <div
            className="p-3 border bg-white"
            style={{ borderRadius: "10px", height: "100%" }}
          >
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: ".08em",
                color: "#0A0A0A",
                marginBottom: 16,
              }}
            >
              IDENTITY
            </p>
            <DetailLine label="Station ID" value={values?.stationID} />
            <DetailLine label="Serial Number" value={values?.serialNumber} />
            {stationType && (
              <DetailLine label="Station Type" value={stationType} />
            )}
          </div>
        </Col>
        <Col md={6}>
          <div
            className="p-3 border bg-white"
            style={{ borderRadius: "10px", height: "100%" }}
          >
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: ".08em",
                color: "#0A0A0A",
                marginBottom: 16,
              }}
            >
              SPECIFICATIONS
            </p>
            <DetailLine label="Vendor" value={values?.vendorName} />
            <DetailLine label="Model" value={values?.model} />
            <DetailLine label="Firmware" value={values?.firmwareVersion} />
            <DetailLine label="OCPP" value={values?.OCPPVersion} />
          </div>
        </Col>
      </Row>

      <p className="fw-semibold mb-3" style={{ fontSize: 16 }}>
        Analytics
      </p>
      <Row className="g-4">
        {/* Revenue Trends */}
        <Col md={12} xl={6}>
          <ChartCard
            title="Revenue Trends"
            color="#0A0A0A"
            onDateRangeChange={handleRevenueRangeChange}
          >
            {revenueTrendLoading ? (
              <ChartSkeleton />
            ) : !revenueApiData || revenueApiData.length === 0 ? (
              <NoDataPlaceholder />
            ) : (
              <ReactApexChart
                type="area"
                height={220}
                options={revenueTrendsOptions}
                series={revenueChartData.series}
              />
            )}
          </ChartCard>
        </Col>

        {/* Usage Trends */}
        <Col md={12} xl={6}>
          <ChartCard
            title="Usage Trends"
            color="#0A0A0A"
            onDateRangeChange={handleUsageRangeChange}
          >
            {usageTrendLoading ? (
              <ChartSkeleton />
            ) : !usageApiData || usageApiData.length === 0 ? (
              <NoDataPlaceholder />
            ) : (
              <ReactApexChart
                type="bar"
                height={220}
                options={usageTrendsOptions}
                series={usageChartData.series}
              />
            )}
          </ChartCard>
        </Col>

        {/* Revenue Forecast */}
        <Col md={12} xl={6}>
          <ForecastChartCard
            title="Revenue Forecast"
            color="#0A0A0A"
            period={forecastRevenuePeriod}
            onPeriodChange={handleForecastRevenuePeriodChange}
          >
            {revenueForecastLoading ? (
              <ChartSkeleton />
            ) : !revenueForecastChartData.categories.length ? (
              <NoDataPlaceholder />
            ) : (
              <ReactApexChart
                type="area"
                height={220}
                options={revenueForecastOptions}
                series={revenueForecastChartData.series}
              />
            )}
          </ForecastChartCard>
        </Col>

        {/* Usage Forecast */}
        <Col md={12} xl={6}>
          <ForecastChartCard
            title="Usage Forecast"
            color="#0A0A0A"
            period={forecastUsagePeriod}
            onPeriodChange={handleForecastUsagePeriodChange}
          >
            {usageForecastLoading ? (
              <ChartSkeleton />
            ) : !usageForecastChartData.categories.length ? (
              <NoDataPlaceholder />
            ) : (
              <ReactApexChart
                type="area"
                height={220}
                options={usageForecastOptions}
                series={usageForecastChartData.series}
              />
            )}
          </ForecastChartCard>
        </Col>
      </Row>
    </div>
  );
};

export default StationDetails;
